import React from 'react';
import { getScoreColor } from '../../../config/scoring';
import { useUnitPreferences, formatWeight, formatWater } from '../../utils/unitConversions';
import { getDailyMetrics, formatDailyDate, type DailyMetricsData } from './dailyMetrics';

/**
 * DailyCard — mobile-friendly "at a glance" daily metrics card.
 *
 * The same metric content as the desktop calendar hover tooltip
 * (CalendarDayTooltip), re-presented in MC's iOS-tile design language for the
 * client Home (today) and History (expanded day) screens. Shares the
 * derivation logic via getDailyMetrics; only the presentation differs.
 *
 * - showScore: Home-today passes false (day in progress → progress, not a grade);
 *   History passes true (settled day → show real/soft score).
 * - showAllMetrics: default false = T2/T3 only (client accountability view).
 */

interface DailyCardProps {
  data: DailyMetricsData;
  showScore?: boolean;
  showAllMetrics?: boolean;
  hideMatchingSoftScore?: boolean;
  userGoals?: any;
  isCoachView?: boolean;
  showDate?: boolean;
  showWeight?: boolean;
}

export const DailyCard: React.FC<DailyCardProps> = ({
  data,
  showScore = true,
  showAllMetrics = false,
  hideMatchingSoftScore = false,
  userGoals = null,
  isCoachView = false,
  showDate = true,
  showWeight = true,
}) => {
  const prefs = useUnitPreferences();
  const { getMetricValue, getGoalTierColor, getSortedMetrics, isGoalActive } = getDailyMetrics(data, {
    showAllMetrics,
    userGoals,
    isCoachView,
  });

  const sortedMetrics = getSortedMetrics();
  const weight = getMetricValue('weight');

  const scoreBadge = (() => {
    if (!showScore) return null;
    const realScore = data.score;
    const softScore = data.softScore;
    const tierStatus = data.tierStatus;

    let display: React.ReactNode = '';
    let displayScore = 0;

    if (tierStatus === 'tier3' && realScore !== null && realScore !== undefined) {
      displayScore = Math.round(realScore);
      const roundedSoft = softScore !== null && softScore !== undefined ? Math.round(softScore) : null;
      const shouldShowSoft = roundedSoft !== null && !(hideMatchingSoftScore && displayScore === roundedSoft);
      display = (
        <>
          <span style={{ color: getScoreColor(displayScore), fontWeight: 700 }}>{displayScore}</span>
          {shouldShowSoft && (
            <>
              {' ('}
              <span style={{ color: getScoreColor(roundedSoft!), fontWeight: 700 }}>{roundedSoft}</span>
              {')'}
            </>
          )}
        </>
      );
    } else if (tierStatus === 'tier2' || tierStatus === 'tier1') {
      const score = softScore !== null && softScore !== undefined ? softScore : realScore;
      if (score !== null && score !== undefined) {
        displayScore = Math.round(score);
        display = (
          <>
            {'('}
            <span style={{ color: getScoreColor(displayScore), fontWeight: 700 }}>{displayScore}</span>
            {')'}
          </>
        );
      }
    } else if (tierStatus === 'none' && realScore !== null && realScore !== undefined) {
      displayScore = Math.round(realScore);
      display = <span style={{ color: getScoreColor(displayScore), fontWeight: 700 }}>{displayScore}</span>;
    }

    if (!display || displayScore <= 0) return null;
    return (
      <div className="mc-daily-card-score">
        <span className="mc-daily-card-score-label">Score</span>
        <span className="mc-daily-card-score-value">{display}</span>
      </div>
    );
  })();

  const energy = data.energy_level;
  const stress = data.stress_level;
  const showEnergy = isGoalActive('daily_energy') && energy !== null && energy !== undefined;
  const showStress = isGoalActive('daily_stress') && stress !== null && stress !== undefined;
  const showWeightStat = showWeight && !!weight;

  return (
    <div className="mc-daily-card">
      {(showDate || scoreBadge) && (
        <div className="mc-daily-card-header">
          {showDate && <span className="mc-daily-card-date">{formatDailyDate(data.date)}</span>}
          {scoreBadge}
        </div>
      )}

      {sortedMetrics.length > 0 ? (
        <div className="mc-daily-card-metrics">
          {sortedMetrics.map((metric) => {
            const value = getMetricValue(metric.dataField)!;
            const targetData = metric.targetField
              ? data.targets_at_time?.[metric.targetField]
              : null;
            const target = (targetData as any)?.goal_value || (targetData as any)?.value || metric.defaultTarget;
            const tierColor = getGoalTierColor(metric.templateId);
            const targetType = (targetData as any)?.goal_type || (targetData as any)?.type || 'target';
            const variance = (targetData as any)?.variance_percent || (targetData as any)?.variance || 10;

            return (
              <div key={metric.templateId} className="mc-daily-card-metric">
                <div className="mc-daily-card-metric-head">
                  <span className="mc-daily-card-metric-name">
                    <span className="mc-daily-card-metric-icon">{metric.icon}</span>
                    <span style={{ color: tierColor !== '#d1d5db' ? tierColor : 'var(--text-secondary)', fontWeight: tierColor !== '#d1d5db' ? 600 : 500 }}>
                      {metric.displayName}
                    </span>
                  </span>
                  <span className="mc-daily-card-metric-value">
                    {metric.templateId === 'daily_water'
                      ? `${formatWater(value, prefs.units.water)} / ${formatWater(target, prefs.units.water)}`
                      : metric.unit
                        ? `${value ?? 0}${metric.unit} / ${target ?? 0}${metric.unit}`
                        : `${(value ?? 0).toLocaleString()} / ${(target ?? 0).toLocaleString()}`}
                  </span>
                </div>
                {metric.renderProgressBar(value, target, targetType, variance)}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mc-daily-card-empty">No tracked metrics logged for this day yet.</div>
      )}

      {(showWeightStat || showEnergy || showStress) && (
        <div className="mc-daily-card-secondary">
          {showWeightStat ? (
            <div className="mc-daily-card-stat">
              <span className="mc-daily-card-stat-icon">⚖️</span>
              <span>{formatWeight(weight, prefs.units.weight)}</span>
            </div>
          ) : null}
          {showEnergy && (
            <div className="mc-daily-card-stat">
              <span className="mc-daily-card-stat-icon">⚡</span>
              <span>Energy {energy}/10</span>
            </div>
          )}
          {showStress && (
            <div className="mc-daily-card-stat">
              <span className="mc-daily-card-stat-icon">😰</span>
              <span>Stress {stress}/10</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
